import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { getAxiosResponse, handleError, downloadFile} from "../../services/authApiRoutes/APImethods";
import APIUrls from "../../services/APIUrls";
import { getRandomColor } from "../../services/Util";
import { PieChart, PieChartProps } from "react-minimal-pie-chart";
import { Chart } from "react-google-charts";
import { transformBlankToNull, dateFormat } from "../../services/Util";
import { RotatingLines } from "react-loader-spinner";
import CustomProgressBar from "../../components/global/CustomProgressBar";
import ReactTooltip from "react-tooltip";
import CustomToolTips from "../../components/global/CustomToolTips";
import Constants from "../../Constants";
import { ConditionalBlock } from "../../ConditionalBlock";
import { ExportIcon } from "../../AppIcons";

export default function OperationalCostReports(props) {
  const { t } = useTranslation();
  const translate = t;
  const intendedUseTooltipRef = useRef(null);
  const purchaseOrderTooltipRef = useRef(null);
  const languageTooltipRef = useRef(null);
  const vendorsTooltipRef = useRef(null);
  const workItemTooltipRef = useRef(null);

  // function makeTooltipContent(
  //   entry: PieChartProps & {
  //     tooltip: PieChartProps['title'];
  //   }
  // ) {
  //   return `Sector ${entry.tooltip} has value ${entry.value}`;
  // }

  const defaultLabelStyle = {
    fontSize: "4px",
    fontFamily: "sans-serif",
    fill: "#FFFFFF",
  };

  const noDataFoundComponent = () => {
    return (
      <div role="rowgroup">
        <div role="row" className="no-data-display align-center">
          <div role="cell">
            <i className="nodata-icon"></i>
            <p>{Constants.NO_RECORD_FOUND}</p>
          </div>
        </div>
      </div>
    );
  };

  const chartoptions = {
    //title: "Language Cost",
    hAxis: { title: "Value", titleTextStyle: { color: "#333" } },
    vAxis: { minValue: 0 },
    chartArea: { width: "50%", height: "35%" },
  };

  const purchaseOrderOptions = [
    { value: "", label: "All" },
    { value: "CUSTOMER_INVOICE_NO_ACTION", label: "Invoice not sent" },
    { value: "CUSTOMER_INVOICE_SENT", label: "Invoice sent" },
    { value: "CUSTOMER_INVOICE_PAID", label: "Invoice paid" },
  ];

  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("");

  const [purchaseOrderStatus, setPurchaseOrderStatus] = useState([]);
  const [top5PurchaseOrderStatus, setTop5PurchaseOrderStatus] = useState([]);
  const [purchaseOrderTotalAmount, setPurchaseOrderTotalAmount] = useState(0);
  const [purchaseOrderTotalCount, setPurchaseOrderTotalCount] = useState(0);
  const [top5PurchaseOrderTotalCount, setTop5PurchaseOrderTotalCount] = useState(0);
  const [purchaseOrderStatusLoading, setPurchaseOrderStatusLoading] = useState(false);
  const [selectedPurchaseOrderOption, setSelectedPurchaseOrderOption] = useState("");

  const [intendedUseStatus, setIntendedUseStatus] = useState([]);
  const [top5IntendedUseStatus, setTop5IntendedUseStatus] = useState([]);
  const [intendedUseTotalAmount, setIntendedUseTotalAmount] = useState(0);
  const [intendedUseTotalCount, setIntendedUseTotalCount] = useState(0);
  const [top5IntendedUseTotalCount, setTop5IntendedUseTotalCount] = useState(0);
  const [intendedUseStatusLoading, setIntendedUseStatusLoading] = useState(false);

  const [workItemStatus, setWorkItemStatus] = useState([]);
  const [top5WorkItemStatus, setTop5WorkItemStatus] = useState([]);
  const [workItemTotalAmount, setWorkItemTotalAmount] = useState(0);
  const [top5WorkItemTotalAmount, setTop5WorkItemTotalAmount] = useState(0);
  const [workItemTotalCount, setWorkItemTotalCount] = useState(0);
  const [workItemStatusLoading, setWorkItemStatusLoading] = useState(false);

  const [vendorsStatus, setVendorsStatus] = useState([]);
  const [top5VendorsStatus, setTop5VendorsStatus] = useState([]);
  const [vendorsTotalAmount, setVendorsTotalAmount] = useState(0);
  const [top5vendorsTotalAmount, setTop5VendorsTotalAmount] = useState(0);
  const [vendorsTotalCount, setVendorsTotalCount] = useState(0);
  const [vendorsStatusLoading, setVendorsStatusLoading] = useState(false);

  const [languageStatus, setLanguageStatus] = useState([]);
  const [filteredLanguageStatus, setFilteredLanguageStatus] = useState([]);
  const [languageTotalAmount, setLanguageTotalAmount] = useState(0);
  const [languageTotalCount, setLanguageTotalCount] = useState(0);
  const [languageStatusLoading, setLanguageStatusLoading] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [downloadInProgress, setDownloadInProgress] = useState(false);

  useEffect(() => {
    fetchLanguages();
    fetchReportData();
  }, []);

  useEffect(() => {
    if (props.selectedDepartment || props.selectedDateRange || props.currency) {
      fetchReportData();
    }
  }, [props.selectedDepartment, props.selectedDateRange, props.currency]);

  useEffect(() => {
    if (selectedPurchaseOrderOption) {
      fetchPurchaseOrderStatus();
    }
  }, [selectedPurchaseOrderOption]);

  useEffect(() => {
    if (selectedLanguage?.value === "") {
      setFilteredLanguageStatus(languageStatus);
    } else {
      setFilteredLanguageStatus(languageStatus?.filter((lang) => lang.title.indexOf(selectedLanguage.label) > -1));
    }
  }, [selectedLanguage]);

  const fetchReportData = () => {
    fetchPurchaseOrderStatus();
    fetchIntendedUseStatus();
    fetchVendorsStatus();
    fetchWorkItemStatus();
    fetchLanguageStatus();
  };

  const fetchLanguages = () => {
    getAxiosResponse(APIUrls.TARGET_LANGUAGE)
      .then((res) => {
        if (res.data.code === 200) {
          const dbLangs = transformBlankToNull(res.data.data);
          dbLangs.unshift({ code: "", display: translate("task_campaign.select_language") });
          const options = dbLangs?.map(({ code, display }) => ({
            label: display,
            value: code,
          }));
          setLanguages(options);
        }
      })
      .catch((err) => {
        handleError(err);
      });
  };

  const fetchIntendedUseStatus = () => {
    setIntendedUseStatusLoading(true);
    let date = `&createdDateRangeStart=${props.startDate}&createdDateRangeEnd=${props.endDate}`;
    getAxiosResponse(APIUrls.INTENDED_USE_REPORT + props.params + date)
      .then((res) => {
        setIntendedUseStatusLoading(false);
        if (res.data.code === 200) {
          let intendedUses = transformBlankToNull(res.data?.data?.report);
          let totalAmount = 0;
          let totalCount = 0;
          intendedUses?.map((intendedUse) => {
            let amount = 0;
            intendedUse.data?.map((data) => {
              amount += data.amount.value;
            });
            intendedUse.value = amount;
            intendedUse.color = getRandomColor();
            totalAmount += amount;
            totalCount += intendedUse.value;
          });
          let filteredData = intendedUses?.filter((data) => data.value !== 0);
          setIntendedUseStatus(filteredData);
          setIntendedUseTotalAmount(totalAmount);
          setIntendedUseTotalCount(totalCount);

          let top5Data = filteredData?.slice(0, 5);
          setTop5IntendedUseStatus(top5Data);
          let top5Count = top5Data?.data?.reduce((count, data) => count += data.amount?.value);
          setTop5IntendedUseTotalCount(top5Count)
        }
      })
      .catch((err) => {
        handleError(err);
        setIntendedUseStatusLoading(false);
      });
  };

  const fetchPurchaseOrderStatus = () => {
    setPurchaseOrderStatusLoading(true);
    let billingStatus = "";
    if (selectedPurchaseOrderOption?.value) {
      billingStatus = `&billingStatus=${selectedPurchaseOrderOption.value}`;
    }
    let date = `&createdDateRangeStart=${props.startDate}&createdDateRangeEnd=${props.endDate}`;
    getAxiosResponse(APIUrls.PURCHASE_ORDER_STATUS_REPORT + props.params + billingStatus + date)
      .then((res) => {
        setPurchaseOrderStatusLoading(false);
        if (res.data?.code === 200) {
          let purchaseOrders = transformBlankToNull(res.data?.data?.report);
          let totalAmount = 0;
          let totalCount = 0;
          purchaseOrders?.map((purchaseOrder) => {
            let amount = 0;
            purchaseOrder.data?.map((data) => {
              amount += data.amount?.value;
            });
            purchaseOrder.color = getRandomColor();
            purchaseOrder.value = amount;
            totalAmount += amount;
            totalCount += purchaseOrder.value;
          });
          let filteredData = purchaseOrders?.filter((data) => data.value !== 0)
          setPurchaseOrderStatus(filteredData);
          setPurchaseOrderTotalAmount(totalAmount);
          setPurchaseOrderTotalCount(totalCount);
          let top5Data = filteredData?.slice(0, 5);
          setTop5PurchaseOrderStatus(top5Data);
          let top5Count = top5Data?.data?.reduce((count, data) => count += data.amount?.value);
          setTop5PurchaseOrderTotalCount(top5Count)
        }
      })
      .catch((err) => {
        handleError(err);
        setPurchaseOrderStatusLoading(false);
      });
  };

  const fetchVendorsStatus = () => {
    setVendorsStatusLoading(true);
    let date = `&createdDateRangeStart=${props.startDate}&createdDateRangeEnd=${props.endDate}`;
    getAxiosResponse(APIUrls.VENDOR_REPORT + props.params + date)
      .then((res) => {
        setVendorsStatusLoading(false);
        if (res.data?.code === 200) {
          let vendors = transformBlankToNull(res.data?.data?.report);
          let totalAmount = 0;
          let totalCount = 0;
          vendors?.map((vendor) => {
            let amount = 0;
            vendor.data?.map((data) => {
              amount += data.amount?.value;
            });
            vendor.value = amount;
            vendor.color = getRandomColor();
            totalAmount += amount;
            totalCount += vendor.value;
          });
          let filteredData = vendors?.filter((data) => data.value !== 0);
          setVendorsStatus(filteredData);
          setVendorsTotalAmount(totalAmount);
          setVendorsTotalCount(totalCount);
          let top5Data = filteredData?.slice(0, 5);
          setTop5VendorsStatus(top5Data);
          let top5Count = top5Data?.data?.reduce((count, data) => count += data.amount?.value);
          setTop5VendorsTotalAmount(top5Count)
        }
      })
      .catch((err) => {
        handleError(err);
        setVendorsStatusLoading(false);
      });
  };

  const fetchWorkItemStatus = () => {
    setWorkItemStatusLoading(true);
    let date = `&createdDateRangeStart=${props.startDate}&createdDateRangeEnd=${props.endDate}`;
    getAxiosResponse(APIUrls.WORK_ITEM_STATUS + props.params + date)
      .then((res) => {
        setWorkItemStatusLoading(false);
        if (res.data?.code === 200) {
          let workItems = transformBlankToNull(res.data?.data?.report);
          let totalAmount = 0;
          let totalCount = 0;
          workItems?.map((workItem) => {
            let amount = 0;
            workItem.data?.map((data) => {
              amount += data.cost?.value;
            });
            workItem.value = amount;
            workItem.color = getRandomColor();
            totalAmount += amount;
            totalCount += workItem.value;
          });
          let filteredData = workItems?.filter((data) => data.value !== 0);
          setWorkItemStatus(filteredData);
          setWorkItemTotalAmount(totalAmount);
          setWorkItemTotalCount(totalCount);

          let top5Data = filteredData?.slice(0, 5);
          setTop5WorkItemStatus(top5Data);
          let top5Count = top5Data?.data?.reduce((count, data) => count += data.cost?.value);
          setTop5WorkItemTotalAmount(top5Count)
        }
      })
      .catch((err) => {
        handleError(err);
        setWorkItemStatusLoading(false);
      });
  };

  const fetchLanguageStatus = () => {
    setLanguageStatusLoading(true);
    let date = `&createdDateRangeStart=${props.startDate}&createdDateRangeEnd=${props.endDate}`;
    getAxiosResponse(APIUrls.LANGUAGE_STATUS_REPORT + props.params + date)
      .then((res) => {
        setLanguageStatusLoading(false);
        if (res.data?.code === 200) {
          let languages = transformBlankToNull(res.data?.data?.report);
          let totalAmount = 0;
          let totalCount = 0;
          languages?.map((language) => {
            let amount = 0.0;
            language.data?.map((data) => {
              let bidAmount = 0.0;
              data.bidItem?.bidItemTasks?.map(obj => bidAmount += obj.cost?.value);
              amount += bidAmount;
            });
            language.value = amount;
            language.color = getRandomColor();
            totalAmount += amount;
            totalCount += language.value;
          });
          setLanguageStatus(languages);
          setFilteredLanguageStatus(languages);
          setLanguageTotalAmount(totalAmount);
          setLanguageTotalCount(totalCount);
        }
      })
      .catch((err) => {
        handleError(err);
        setLanguageStatusLoading(false);
      });
  };

  const downloadIntendedUseStatusReport = () =>{
    let date = `&createdDateRangeStart=${props.startDate}&createdDateRangeEnd=${props.endDate}`;
    let url = APIUrls.INTENDED_USE_REPORT_EXPORT + props.params + date;
    downloadReport(url,'Spending_by_Intended_Use.xlsx');
  }

  const downloadPurchaseOrderStatusReport = () =>{
    let billingStatus = "";
    if(selectedPurchaseOrderOption?.value) {
        billingStatus = `&billingStatus=${selectedPurchaseOrderOption.value}`;
    }
    let date = `&createdDateRangeStart=${props.startDate}&createdDateRangeEnd=${props.endDate}`;
    let url = APIUrls.PURCHASE_ORDER_STATUS_REPORT_EXPORT + props.params + billingStatus + date;
    downloadReport(url,'Spending_by_Purchase_Order.xlsx');
  }

  const downloadVendorStatusReport = () =>{
    let date = `&createdDateRangeStart=${props.startDate}&createdDateRangeEnd=${props.endDate}`;
    let url = APIUrls.VENDOR_REPORT_EXPORT + props.params + date;
    downloadReport(url,'Spending_by_Vendor.xlsx');
  }

  const downloadWorkItemStatusReport = () =>{
    let date = `&createdDateRangeStart=${props.startDate}&createdDateRangeEnd=${props.endDate}`;
    let url = APIUrls.WORK_ITEM_STATUS_EXPORT + props.params + date;
    downloadReport(url,'Spending_by_Work_Item.xlsx');
  }

  const downloadLanguageStatusReport = () =>{
    let date = `&createdDateRangeStart=${props.startDate}&createdDateRangeEnd=${props.endDate}`;
    let url = APIUrls.LANGUAGE_STATUS_REPORT_EXPORT + props.params + date;
    downloadReport(url,'Spending_by_Language.xlsx');
  }


  const downloadReport = (url, reportName) =>{
    setDownloadInProgress(true);
    downloadFile(url,reportName)
    .then((res)=>{
        setDownloadInProgress(false);
    })
    .catch(err=>{
        setDownloadInProgress(false);
        handleError(err);
    }) ;
  }

  return (
    <div className="flex gap20 operational-tab-content">
      <div className="left-report p-0 flex gap20">
        <div className="report-order-item project-status report-border">
          <div className="flex gap20 mar-bot10 report-heading">
            <h5 className="flex flex-nowrap">
              <span>By Intended Use</span>
              <CustomToolTips dataTip={Constants.OPERATIONAL_COST.INTENDED_USE_TOOLTIPS} toolTipRef={intendedUseTooltipRef} toolTipId="intendedUse" />
            </h5>
            <button className="download-file" onClick={downloadIntendedUseStatusReport}>{ExportIcon}</button>
          </div>
          {intendedUseStatusLoading ? (
            <div style={{ textAlign: "center" }} className="flex-align-center loader-center">
              <RotatingLines strokeColor="grey" strokeWidth="5" width="96" visible={true} />
            </div>
          ) : (
            <>
              <ConditionalBlock condition={intendedUseTotalCount === 0}>{noDataFoundComponent()}</ConditionalBlock>
              <ConditionalBlock condition={intendedUseTotalCount > 0}>
                <div className="pie-chart flex flex-align-center">
                  <div className="chartBox">
                    <div className="pieTotalCount">
                      <span>{props.currency?.symbol}{Math.round(intendedUseTotalAmount)}</span>
                      <strong>TOTAL</strong>
                    </div>
                    <PieChart
                      radius={35}
                      data={top5IntendedUseStatus !== null ? top5IntendedUseStatus : []}
                      //onFocus={(e, segmentIndex) => }
                      segmentsTabIndex={0}
                      center={[40, 50]}
                      viewBoxSize={[100, 100]}
                      lineWidth={40}
                      totalValue={top5IntendedUseTotalCount}
                      labelPosition={78}
                      label={({ dataEntry }) => `${Math.floor(dataEntry.percentage)} %`}
                      labelStyle={{
                        ...defaultLabelStyle,
                      }}
                    />
                  </div>
                  <div className="clorList maxHeight320">
                    <ul>
                      {intendedUseStatus?.map((intendedUse) => {
                        return (
                          <li>
                            <span style={{ background: intendedUse.color }}> </span>
                            <small>{props.currency?.symbol}{Math.round(intendedUse.value) + " : " + intendedUse.title}</small>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </ConditionalBlock>
            </>
          )}
        </div>

        <div className="report-order-item project-status report-border">
          <div className="flex gap20 mar-bot10 report-heading">
            <h5 className="flex flex-nowrap">
              <span>By Purchase Order</span>
              <CustomToolTips dataTip={Constants.OPERATIONAL_COST.PURCHASE_ORDER_TOOLTIPS} toolTipRef={purchaseOrderTooltipRef} toolTipId="purchaseOrder" />
            </h5>

            <div className="global-dropdown-block m-0">
              <Select
                className="global-dropdown-select"
                menuShouldScrollIntoView={false}
                closeMenuOnSelect={true}
                classNamePrefix="cw"
                options={purchaseOrderOptions}
                value={selectedPurchaseOrderOption === "" ? purchaseOrderOptions[0] : selectedPurchaseOrderOption}
                onChange={(event) => setSelectedPurchaseOrderOption(event)}
              />
            </div>
            <button className="download-file" onClick={downloadPurchaseOrderStatusReport}>{ExportIcon}</button>
          </div>

          {purchaseOrderStatusLoading ? (
            <div style={{ textAlign: "center" }} className="flex-align-center loader-center">
              <RotatingLines strokeColor="grey" strokeWidth="5" width="96" visible={true} />
            </div>
          ) : (
            <>
              <ConditionalBlock condition={purchaseOrderTotalCount === 0}>{noDataFoundComponent()}</ConditionalBlock>
              <ConditionalBlock condition={purchaseOrderTotalCount > 0}>
                <div className="pie-chart flex flex-align-center">
                  <div className="chartBox">
                    <div className="pieTotalCount">
                      <span>{props.currency?.symbol}{Math.round(purchaseOrderTotalAmount)}</span>
                      <strong>TOTAL</strong>
                    </div>
                    <PieChart
                      radius={35}
                      data={top5PurchaseOrderStatus !== null ? top5PurchaseOrderStatus : []}
                      segmentsTabIndex={0}
                      center={[40, 50]}
                      viewBoxSize={[100, 100]}
                      lineWidth={40}
                      totalValue={top5PurchaseOrderTotalCount}
                      labelPosition={78}
                      label={({ dataEntry }) => `${Math.floor(dataEntry.percentage)} %`}
                      labelStyle={{
                        ...defaultLabelStyle,
                      }}></PieChart>
                  </div>
                  <div className="clorList maxHeight320">
                    <ul>
                      {purchaseOrderStatus?.map((purchaseOrder) => {
                        return (
                          <li>
                            <span style={{ background: purchaseOrder.color }}> </span>
                            <small>{props.currency?.symbol}{Math.round(purchaseOrder.value) + " : " + purchaseOrder.title}</small>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </ConditionalBlock>
            </>
          )}
        </div>

        <div className="report-order-item project-status report-border">
          <div className="flex gap20 mar-bot10 report-heading">
            <h5 className="flex flex-nowrap">
              <span>By Vendors</span>
              <CustomToolTips dataTip={Constants.OPERATIONAL_COST.VENDORS_TOOLTIPS} toolTipRef={vendorsTooltipRef} toolTipId="vendors" />
            </h5>
            <button className="download-file" onClick={downloadVendorStatusReport}>{ExportIcon}</button>
          </div>
          {vendorsStatusLoading ? (
            <div style={{ textAlign: "center" }} className="flex-align-center loader-center">
              <RotatingLines strokeColor="grey" strokeWidth="5" width="96" visible={true} />
            </div>
          ) : (
            <>
              <ConditionalBlock condition={vendorsTotalAmount === 0}>{noDataFoundComponent()}</ConditionalBlock>
              <ConditionalBlock condition={vendorsTotalAmount > 0}>
                <div className="pie-chart flex flex-align-center">
                  <div className="chartBox">
                    <div className="pieTotalCount">
                      <span>{props.currency?.symbol}{Math.round(vendorsTotalAmount)}</span>
                      <strong>TOTAL</strong>
                    </div>
                    <PieChart
                      radius={35}
                      data={top5VendorsStatus !== null ? top5VendorsStatus : []}
                      segmentsTabIndex={0}
                      center={[40, 50]}
                      viewBoxSize={[100, 100]}
                      lineWidth={40}
                      totalValue={top5vendorsTotalAmount}
                      labelPosition={78}
                      label={({ dataEntry }) => `${Math.floor(dataEntry.percentage)} %`}
                      labelStyle={{ ...defaultLabelStyle }}></PieChart>
                  </div>
                  <div className="clorList maxHeight320">
                    <ul>
                      {vendorsStatus?.map((vendor) => {
                        return (
                          <li>
                            <span style={{ background: vendor.color }}> </span>
                            <small>{props.currency?.symbol}{Math.round(vendor.value) + " : " + vendor.title}</small>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </ConditionalBlock>
            </>
          )}
        </div>

        <div className="report-order-item project-status report-border">
          <div className="flex gap20 mar-bot10 report-heading">
            <h5 className="flex flex-nowrap">
              <span>By Work Item</span>
              <CustomToolTips dataTip={Constants.OPERATIONAL_COST.WORK_ITEM_TOOLTIPS} toolTipRef={workItemTooltipRef} toolTipId="workItem" />
            </h5>
            <button className="download-file" onClick={downloadWorkItemStatusReport}>{ExportIcon}</button>
          </div>
          {workItemStatusLoading ? (
            <div style={{ textAlign: "center" }} className="flex-align-center loader-center">
              <RotatingLines strokeColor="grey" strokeWidth="5" width="96" visible={true} />
            </div>
          ) : (
            <>
              <ConditionalBlock condition={workItemTotalAmount === 0}>{noDataFoundComponent()}</ConditionalBlock>
              <ConditionalBlock condition={workItemTotalAmount > 0}>
                <div className="pie-chart flex flex-align-center">
                  <div className="chartBox">
                    <div className="pieTotalCount">
                      <span>{props.currency?.symbol}{Math.round(workItemTotalAmount)}</span>
                      <strong>TOTAL</strong>
                    </div>
                    <PieChart
                      radius={35}
                      data={top5WorkItemStatus !== null ? top5WorkItemStatus : []}
                      segmentsTabIndex={0}
                      center={[40, 50]}
                      viewBoxSize={[100, 100]}
                      lineWidth={40}
                      totalValue={top5WorkItemTotalAmount}
                      labelPosition={78}
                      label={({ dataEntry }) => `${Math.floor(dataEntry.percentage)} %`}
                      labelStyle={{ ...defaultLabelStyle }}></PieChart>
                  </div>
                  <div className="clorList maxHeight320">
                    <ul>
                      {workItemStatus?.map((workItem) => {
                        return (
                          <li>
                            <span style={{ background: workItem.color }}> </span>
                            <small>{props.currency?.symbol}{Math.round(workItem.value) + " : " + translate("report.op_cost.work_item." + workItem.title)}</small>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              </ConditionalBlock>
            </>
          )}
        </div>
      </div>

      <div className="right-status">
        <div className="lang-progress report-border">
          <div className="flex gap20 mar-bot10 report-heading">
            <h5 className="flex flex-nowrap">
              <span>By Language</span>
              <CustomToolTips dataTip={Constants.OPERATIONAL_COST.LANGUAGE_TOOLTIPS} toolTipRef={languageTooltipRef} toolTipId="language" />
            </h5>
            <button className="download-file" onClick={downloadLanguageStatusReport}>{ExportIcon}</button>
          </div>
          <div className="total-amount flex mar-bot20">
            <strong>Total Amount:</strong>&nbsp; {props.currency?.symbol} {languageStatusLoading ? '--' : Math.round(languageTotalAmount)}
          </div>
          <div className="flex wrap-dropdown mar-bot20">
            <div className="global-dropdown-block w-d100">
              <label>What is your language?</label>
              <Select
                classNamePrefix="cw"
                className="global-dropdown-select"
                menuShouldScrollIntoView={false}
                closeMenuOnSelect={true}
                options={languages}
                value={selectedLanguage === "" ? languages[0] : selectedLanguage}
                onChange={(event) => setSelectedLanguage(event)}
              />
            </div>
          </div>
          {languageStatusLoading ? (
            <div style={{ textAlign: "center" }} className="flex-align-center loader-center">
              <RotatingLines strokeColor="grey" strokeWidth="5" width="96" visible={true} />
            </div>
          ) : (
            <>
              <ConditionalBlock condition={filteredLanguageStatus === undefined || filteredLanguageStatus === null || filteredLanguageStatus?.length === 0}>{noDataFoundComponent()}</ConditionalBlock>
              <ConditionalBlock condition={filteredLanguageStatus?.length > 0}>
                <ul className="report-order-list" style={{maxHeight: '450px', overflowY: 'auto'}} >
                  {filteredLanguageStatus?.map(
                    (data) =>
                      data.value > 0 && (
                        <li>
                          <span>{data?.title}</span>
                          <strong>{props.currency?.symbol} {Math.round(data?.value)}</strong>&nbsp;&nbsp;
                          <div aria-label={data?.value} className="font-bold">
                            <CustomProgressBar
                              completed={data?.value !== "" ? Math.round(data?.value) / 100 : 0}
                              labelSize="14px"
                              labelLineHeight="0px"
                              width="40px"
                              labelClassName="progressBarLabel"
                              borderRadius="50px"
                              labelAlignment="right"
                              labelColor={data?.color}
                              customLabel=""
                              height="10px"
                              className=""
                              bgColor={data?.color}
                              baseBgColor="#E6E6E6"
                            />
                          </div>
                        </li>
                      )
                  )}
                </ul>
              </ConditionalBlock>
            </>
          )}
        </div>
      </div>
      <div className={downloadInProgress ? "gb-overlay show" : "gb-overlay hide"}>
        <div className="gb-loader">
            <RotatingLines strokeColor="#0096FF" strokeWidth="5" width="196" visible={true} />
        </div>
      </div>
    </div>
  );
}
